import { createAction } from "redux-actions";
import axios from "axios";
import { setLocale } from "react-redux-i18n";
import { TYPE_CHAR } from "../../constants";
import {
  getData,
  getLevel,
  getFocus,
  getId,
  getShowHints,
  getSolved,
  getValue,
  hadFocus,
  hasValue,
  isCorrect,
  isFocusable,
  getLang,
  getSeed,
  getSecret
} from "./selectors";
import { stateId } from "../../helpers/id";
import { togglePaywall } from "../ui/actions";
import { addPrizesToPlayer } from "../user/actions";

export const FETCH = "crossword/fetch";
export const FETCH_SUCCESS = "crossword/fetch_success";
export const FETCH_FAILURE = "crossword/fetch_failure";
export const SET_SOLVED = "crossword/set_solved";
export const SET_FOCUS = "crossword/set_focus";
export const SET_VALUE = "crossword/set_value";
export const CLEAR_VALUES = "crossword/clear_values";

export const fetchStart = createAction(FETCH);
export const fetchSuccess = createAction(FETCH_SUCCESS);
export const fetchFailure = createAction(FETCH_FAILURE);
export const setSolved = createAction(SET_SOLVED);
export const clearValues = createAction(CLEAR_VALUES);

export const fetchCrossword = (lang, seed, level, secret) => (
  dispatch,
  getState
) => {
  const state = getState();
  const currentLang = getLang(state);
  const currentId = getId(state);
  const id = stateId({ lang, seed, level, secret });

  // Only fetch new data if ID differs from state
  if (currentId !== id) {
    const config = {
      params: {
        lang,
        seed: seed || undefined,
        level,
        secret: secret || undefined
      }
    };

    dispatch(fetchStart());

    return axios
      .get("/api/generate", config)
      .then(({ data }) => {
        if (currentLang !== lang) {
          dispatch(setLocale(lang));
        }
        dispatch(fetchSuccess(data));
      })
      .catch(err => {
        if (err.request.status === 401) {
          dispatch(togglePaywall(true));
        }

        dispatch(fetchFailure(err));
      });
  }

  return Promise.resolve();
};

export const setFocusedValue = value => (dispatch, getState) => {
  const { x, y } = getFocus(getState());
  if (x && y) {
    dispatch(setValue(x, y, value));
    dispatch(focusNext(x, y));
  }
};

export const setValue = (x, y, value) => (dispatch, getState) => {
  const state = getState();
  const correct = getShowHints(state, x, y) && isCorrect(state, x, y);

  if (!correct) {
    dispatch({
      type: SET_VALUE,
      payload: {
        x,
        y,
        value: value.toUpperCase()
      }
    });
    dispatch(checkIfSolved());
  }
};

export const checkIfSolved = () => (dispatch, getState) => {
  const state = getState();
  const data = getData(state);
  const prevSolved = getSolved(state);

  if (data) {
    const solved = data.every(cell => {
      const { x, y, type, char } = cell;

      if (type === TYPE_CHAR) {
        return getValue(state, x, y) === char;
      }

      return true;
    });

    if (solved !== prevSolved) {
      dispatch(postSolution());
    }
  }
};

export const postSolution = () => (dispatch, getState) => {
  const state = getState();
  const cells = getData(state);
  const level = getLevel(state);
  const lang = getLang(state);
  const seed = getSeed(state);
  const secret = getSecret(state);

  const data = cells.map(cell => ({
    ...cell,
    value: getValue(state, cell.x, cell.y)
  }));

  return axios
    .post("/api/solution", {
      lang,
      seed,
      level,
      secret,
      data
    })
    .then(({ data }) => {
      dispatch(setSolved(data));

      if (data.solved && data.prizes) {
        dispatch(addPrizesToPlayer(data.prizes));
      }
    });
};

export const setFocus = (x, y) => dispatch =>
  dispatch({
    type: SET_FOCUS,
    payload: {
      x,
      y
    }
  });

export const focusNext = (x, y) => (dispatch, getState) => {
  const state = getState();

  const goingHor = hadFocus(state, x - 1, y);
  const hasPrevVerValue = hasValue(state, x, y - 1);
  const hasNextHorChar = isFocusable(state, x + 1, y);
  const hasNextVerChar = isFocusable(state, x, y + 1);

  if (hasNextHorChar && (goingHor || !hasPrevVerValue || !hasNextVerChar)) {
    dispatch(setFocus(x + 1, y));
  } else if (hasNextVerChar) {
    dispatch(setFocus(x, y + 1));
  } else {
    dispatch(setFocus(null, null));
  }
};

export const focusPrev = (x, y) => (dispatch, getState) => {
  const state = getState();

  const hasPrevHorValue = hasValue(state, x - 1, y);
  const hasPrevVerValue = hasValue(state, x, y - 1);

  const hasPrevHorChar = isFocusable(state, x - 1, y);
  const hasPrevVerChar = isFocusable(state, x, y - 1);

  if (hasPrevHorValue) {
    dispatch(setFocus(x - 1, y));
  } else if (hasPrevVerValue) {
    dispatch(setFocus(x, y - 1));
  } else if (hasPrevHorChar) {
    dispatch(setFocus(x - 1, y));
  } else if (hasPrevVerChar) {
    dispatch(setFocus(x, y - 1));
  }
};

export const focusLeft = (x, y) => (dispatch, getState) => {
  const state = getState();

  if (isFocusable(state, x - 1, y)) {
    dispatch(setFocus(x - 1, y));
  }
};

export const focusRight = (x, y) => (dispatch, getState) => {
  const state = getState();

  if (isFocusable(state, x + 1, y)) {
    dispatch(setFocus(x + 1, y));
  }
};

export const focusUp = (x, y) => (dispatch, getState) => {
  const state = getState();

  if (isFocusable(state, x, y - 1)) {
    dispatch(setFocus(x, y - 1));
  }
};

export const focusDown = (x, y) => (dispatch, getState) => {
  const state = getState();

  if (isFocusable(state, x, y + 1)) {
    dispatch(setFocus(x, y + 1));
  }
};



// WEBPACK FOOTER //
// ./src/reducers/crossword/actions.js